import { CSSProperties } from "react";

export * from "./pricing";
export * from "./elementThickness";
export * from "./sync/isNewerVersion";
export * from "./sync/foldChanges";

export interface PdfToPngResponse {
  ConversionCost: number;
  Files: File[];
}

export interface File {
  FileName: string;
  FileExt: string;
  FileSize: number;
  FileId: string;
  Url: string;
}

export enum PageFormat {
  A3_LANDSCAPE = "A3_LANDSCAPE",
  A3_PORTRAIT = "A3_PORTRAIT",
  A4_LANDSCAPE = "A4_LANDSCAPE",
  A4_PORTRAIT = "A4_PORTRAIT",
  LETTER_LANDSCAPE = "LETTER_LANDSCAPE",
  LETTER_PORTRAIT = "LETTER_PORTRAIT",
  TABLOID_LANDSCAPE = "TABLOID_LANDSCAPE",
  TABLOID_PORTRAIT = "TABLOID_PORTRAIT",
  ARCH_C_LANDSCAPE = "ARCH_C_LANDSCAPE",
  ARCH_C_PORTRAIT = "ARCH_C_PORTRAIT",
  ARCH_D_LANDSCAPE = "ARCH_D_LANDSCAPE",
  ARCH_D_PORTRAIT = "ARCH_D_PORTRAIT",
}

export type SheetName = "A3" | "A4" | "LETTER" | "TABLOID" | "ARCH_C" | "ARCH_D";

// Physical sheet dimensions in millimetres, independent of orientation.
export const SHEET_SIZES: Record<
  SheetName,
  { longEdgeMm: number; shortEdgeMm: number }
> = {
  A3: { longEdgeMm: 420, shortEdgeMm: 297 },
  A4: { longEdgeMm: 297, shortEdgeMm: 210 },
  LETTER: { longEdgeMm: 279.4, shortEdgeMm: 215.9 },
  TABLOID: { longEdgeMm: 431.8, shortEdgeMm: 279.4 },
  ARCH_C: { longEdgeMm: 609.6, shortEdgeMm: 457.2 },
  ARCH_D: { longEdgeMm: 914.4, shortEdgeMm: 609.6 },
};

const PAGE_FORMAT_SHEET: Record<PageFormat, SheetName> = {
  [PageFormat.A3_LANDSCAPE]: "A3",
  [PageFormat.A3_PORTRAIT]: "A3",
  [PageFormat.A4_LANDSCAPE]: "A4",
  [PageFormat.A4_PORTRAIT]: "A4",
  [PageFormat.LETTER_LANDSCAPE]: "LETTER",
  [PageFormat.LETTER_PORTRAIT]: "LETTER",
  [PageFormat.TABLOID_LANDSCAPE]: "TABLOID",
  [PageFormat.TABLOID_PORTRAIT]: "TABLOID",
  [PageFormat.ARCH_C_LANDSCAPE]: "ARCH_C",
  [PageFormat.ARCH_C_PORTRAIT]: "ARCH_C",
  [PageFormat.ARCH_D_LANDSCAPE]: "ARCH_D",
  [PageFormat.ARCH_D_PORTRAIT]: "ARCH_D",
};

const isLandscapeFormat = (pageFormat: PageFormat): boolean =>
  pageFormat.endsWith("_LANDSCAPE");

export const getSheetName = (pageFormat: PageFormat): SheetName =>
  PAGE_FORMAT_SHEET[pageFormat];

// Canonical paper pixel size for a page format: the long edge is pinned to
// 1600px so that formats sharing the same aspect ratio (e.g. A3/A4) share
// the same pixel dimensions, as they did before non-ISO formats existed.
export const getPaperPixelDims = (
  pageFormat: PageFormat,
): { width: number; height: number } => {
  const { longEdgeMm, shortEdgeMm } = SHEET_SIZES[getSheetName(pageFormat)];
  const longEdgePx = 1600;
  const shortEdgePx = (1600 * shortEdgeMm) / longEdgeMm;
  return isLandscapeFormat(pageFormat)
    ? { width: longEdgePx, height: shortEdgePx }
    : { width: shortEdgePx, height: longEdgePx };
};

export enum DrawingType {
  SITE_PLAN = "siteplan",
  FLOOR_PLAN = "floorplan",
  FACADE_DRAWING = "facade_drawing",
  OTHER = "other",
}

export const DrawingTypeSwedish: { [key in DrawingType]: string } = {
  siteplan: "Situationsplan",
  floorplan: "Planritning",
  facade_drawing: "Fasadritning",
  other: "Tom",
};

export interface Canvas {
  canvasId: string;
  userId: number;
  name: string;
  commune: string;
  createdAt: string;
  updatedAt: string;
  mapUrl: string;
  author: string;
  pageFormat: PageFormat;
  authorExportedAt: string;
  drawingType: DrawingType;
  // drawingHead* are used on the title block
  showDrawingHead: boolean;
  drawingHeadType: string;
  drawingHeadScale: string;
  drawingHeadPropertyReference: string;
  northArrowRotation: number;
  showNorthArrow: boolean;
  unitSystem: UnitSystem;
  lengthFormat: LengthFormat;
}

export interface CanvasAndUser extends Canvas {
  user?: User;
  changeCount?: number;
}

export type CanvasExport = Pick<
  Canvas,
  | "author"
  | "authorExportedAt"
  | "drawingHeadScale"
  | "drawingHeadPropertyReference"
>;

export enum Role {
  User = "user",
  Admin = "admin",
}

export const SUPPORTED_LOCALES = ["sv", "en"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const SUPPORTED_UNIT_SYSTEMS = ["metric", "imperial"] as const;
export type UnitSystem = (typeof SUPPORTED_UNIT_SYSTEMS)[number];

// How a length is written out, independent of unitSystem. "meters" is the
// column default so every existing (metric) project is backward compatible
// without a backfill; metric projects pick meters or millimeters and
// imperial projects feet-inches or decimal-feet explicitly. Formats that
// don't belong to the project's unit system are resolved away at render
// time (see resolveLengthFormat), so no backfill is needed when switching.
export const SUPPORTED_LENGTH_FORMATS = [
  "meters",
  "millimeters",
  "feet-inches",
  "decimal-feet",
] as const;
export type LengthFormat = (typeof SUPPORTED_LENGTH_FORMATS)[number];

export const SUPPORTED_SITES = ["ritabygglov", "drawblueprint"] as const;
export type Site = (typeof SUPPORTED_SITES)[number];

export const SITE_DOMAINS: Record<Site, string> = {
  ritabygglov: "ritabygglov.se",
  drawblueprint: "drawblueprint.com",
};

export interface User {
  userId: number;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  lastSeenAt: string;
  activeDays: number;
  roles: Role[];
  isAcceptingMarketing: boolean;
  locale: Locale;
  site: Site;
  subscriptions?: Subscription[];
}

export type ListItem = {
  label: string;
  value: boolean;
  url?: string;
};

export type Checklist = {
  checklistId: string;
  description: string;
  title: string;
  subTitle: string;
  items: ListItem[];
  canvasId: string;
  userId: number;
  createdAt: string;
  updatedAt: string;
};

export type Subscription = {
  subscriptionId: string;
  userId: number;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  stripeSessionId: string | null;
  status:
  | "incomplete"
  | "incomplete_expired"
  | "trialing"
  | "active"
  | "past_due"
  | "paused"
  | "canceled"
  | "unpaid";
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAt: string;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  updatedAt: string;
};

export enum ElementState {
  Visible = "visible",
  Hidden = "hidden",
  Creation = "creation",
}

export enum ThicknessAnchor {
  Outer = "outer",
  Centre = "centre",
  Inner = "inner",
}

export interface ElementBase {
  id: string;
  style?: CSSProperties;
  state: ElementState;
  rotate: number;
  renderingOrder: number;
  text: string;
  userVersion: UserVersion;
  showDimensions: boolean;
  pattern?: Pattern;
  patternSize?: number;
  thickness: number;
  thicknessAnchor: ThicknessAnchor;
}

// TODO: Add some other type that unifies all of these shapes, some thing like "renderType: Square"
export interface Rect extends ElementBase {
  type: ElementType.Rect;
  width: number;
  height: number;
  x: number;
  y: number;
  diagonalHatch: boolean;
  isOutlined?: boolean;
  dimensionOffsetWidth?: number;
  dimensionOffsetHeight?: number;
}

export interface Text extends Omit<Rect, "type"> {
  type: ElementType.Text;
}

export interface Triangle extends ElementBase {
  type: ElementType.Triangle;
  pointRatio: number;
  x: number;
  y: number;
  width: number;
  height: number;
  diagonalHatch: boolean;
  isOutlined?: boolean;
  dimensionOffsetWidth?: number;
  dimensionOffsetHeight?: number;
  dimensionOffsetLeftEdge?: number;
  dimensionOffsetRightEdge?: number;
}

export interface Angle extends Omit<Triangle, "type"> {
  type: ElementType.Angle;
}

export interface Trapezoid extends ElementBase {
  type: ElementType.Trapezoid;
  topWidth: number;
  bottomWidth: number;
  height: number;
  x: number;
  y: number;
  width: number;
  diagonalHatch: boolean;
  isOutlined?: boolean;
  dimensionOffsetTopWidth?: number;
  dimensionOffsetBottomWidth?: number;
  dimensionOffsetHeight?: number;
  dimensionOffsetLeftEdge?: number;
  dimensionOffsetRightEdge?: number;
}

export interface Group extends ElementBase {
  type: ElementType.Group;
  x: number;
  y: number;
  width: number;
  height: number;
  childIds: string[];
}

export enum DimensionSide {
  Above = "above",
  Below = "below",
}

// "Point" for use in Polyline,
// for general point, see Coordinate in app/src/types.ts
export interface Point {
  x: number;
  y: number;
  connectingPointIndex?: number;
  dimensionSide: DimensionSide;
  dimensionOffset?: number;
  cornerRadius?: number;
}

export enum LineType {
  Line = "Line",
  Dimension = "Dimension",
}

export const PatternMapping = {
  NONE: "none",
  WHITE: "white",
  DIAGONAL_HATCH: "diagonalHatch",
  BRICK_WALL: "brickWall",
  ROOF_TILES: "roofTiles",
  STAGGED_SQUARE_WALL: "staggeredSquareWall",
  WOOD_PANEL: "woodPanel",
  WOOD_PANEL_HORIZONTAL: "woodPanelHorizontal",
  DARK_GREY: "darkGrey",
  LIGHT_GREY: "lightGrey",
  CIRCULAR_DOTS: "circularDots",
  BLACK: "black",
  RED: "red",
  ORANGE: "orange",
  YELLOW: "yellow",
  GREEN: "green",
  BLUE: "blue",
} as const;

export type Pattern = (typeof PatternMapping)[keyof typeof PatternMapping];

export const PATTERN_SIZE_MULTIPLIERS = {
  S: 0.75,
  M: 1,
  L: 1.5,
} as const;

export type PatternSizeLabel = keyof typeof PATTERN_SIZE_MULTIPLIERS;

export enum StrokeOptions {
  black = "#000",
  green = "#00794C",
  red = "#9B030A",
  yellow = "#FFC65B",
  blue = "#04DBF3",
  orange = "#FF6529",
  white = "#FFFFFF",
  none = "none",
}

export const COLOR_VALUES = Object.values(StrokeOptions);

export interface Polyline extends ElementBase {
  type: ElementType.Polyline;
  lineType: LineType;
  points: Point[];
  fill?: string;
  stroke: string;
  leftArrow?: ArrowOption;
  rightArrow?: ArrowOption;
  isOutlined: boolean;
  diagonalHatch: boolean;
}

export enum ArrowOption {
  none = "none",
  slim = "slim",
  thick = "thick",
}

export enum ChangeType {
  Create = "create",
  Update = "update",
  Move = "move",
  Delete = "delete",
}

export enum ElementType {
  Rect = "rect",
  Text = "text",
  Polyline = "polyline",
  Symbol = "symbol",
  Fixture = "fixture",
  Triangle = "triangle",
  Trapezoid = "trapezoid",
  Angle = "angle",
  Group = "group",
}

// identical to the legacy STROKE_WIDTHS strings ('1px' | '3px' |
// '8px' | '16px')
export const THICKNESS_PRESETS = {
  S: 1,
  M: 3,
  L: 8,
  XL: 16,
} as const;

export const DEFAULT_THICKNESS = 2;

// Anchor for newly created elements
export const DEFAULT_ANCHOR_BY_TYPE: Record<ElementType, ThicknessAnchor> = {
  [ElementType.Rect]: ThicknessAnchor.Outer,
  [ElementType.Triangle]: ThicknessAnchor.Outer,
  [ElementType.Trapezoid]: ThicknessAnchor.Outer,
  [ElementType.Angle]: ThicknessAnchor.Centre,
  [ElementType.Polyline]: ThicknessAnchor.Centre,
  [ElementType.Text]: ThicknessAnchor.Centre,
  [ElementType.Symbol]: ThicknessAnchor.Centre,
  [ElementType.Fixture]: ThicknessAnchor.Centre,
  // A Group draws no stroke of its own — its bbox is a selection affordance,
  // not geometry — so this entry is inert and only satisfies the Record.
  [ElementType.Group]: ThicknessAnchor.Centre,
};


// Legacy table for old canvases with missing thicknessAnchor
export const LEGACY_ANCHOR_BY_TYPE: Record<ElementType, ThicknessAnchor> = {
  [ElementType.Rect]: ThicknessAnchor.Outer,
  [ElementType.Triangle]: ThicknessAnchor.Centre,
  [ElementType.Trapezoid]: ThicknessAnchor.Centre,
  [ElementType.Angle]: ThicknessAnchor.Centre,
  [ElementType.Polyline]: ThicknessAnchor.Centre,
  [ElementType.Text]: ThicknessAnchor.Centre,
  [ElementType.Symbol]: ThicknessAnchor.Centre,
  [ElementType.Fixture]: ThicknessAnchor.Centre,
  [ElementType.Group]: ThicknessAnchor.Centre,
};

export enum SymbolType {
  GENERAL_WELL = "GENERAL_WELL",
  WELL = "WELL",
  THREE_CHAMBER_WELL = "THREE_CHAMBER_WELL",
  DRILL_HOLE = "DRILL_HOLE",
}

export interface Symbol extends ElementBase {
  type: ElementType.Symbol;
  width: number;
  height: number;
  x: number;
  y: number;
  symbolType: SymbolType;
}

export enum FixtureType {
  WINDOW_PLAN = "WINDOW_PLAN",
  WINDOW_ELEVATION = "WINDOW_ELEVATION",
  WINDOW_ELEVATION_OPENABLE = "WINDOW_ELEVATION_OPENABLE",
  DOOR_PLAN = "DOOR_PLAN",
  DOOR_ELEVATION = "DOOR_ELEVATION",
  DOUBLE_DOOR_PLAN = "DOUBLE_DOOR_PLAN",
  DOUBLE_DOOR_ELEVATION = "DOUBLE_DOOR_ELEVATION",
  SLIDING_DOOR_PLAN = "SLIDING_DOOR_PLAN",
  SLIDING_DOOR_ELEVATION = "SLIDING_DOOR_ELEVATION",
  ROOF_ANGLE = "ROOF_ANGLE",
  STRAIGHT_STAIR_PLAN = "STRAIGHT_STAIR_PLAN",
  DOUBLE_STAIR_PLAN = "DOUBLE_STAIR_PLAN",
  CORNER_STAIR_PLAN = "CORNER_STAIR_PLAN",
  U_STAIR_PLAN = "U_STAIR_PLAN",
}

export interface Fixture extends ElementBase {
  type: ElementType.Fixture;
  width: number;
  height: number;
  x: number;
  y: number;
  fixtureType: FixtureType;
  mirroredX?: boolean;
  mirroredY?: boolean;
}

export type Element =
  | Rect
  | Text
  | Polyline
  | Symbol
  | Fixture
  | Triangle
  | Trapezoid
  | Angle
  | Group;

export type RectLikeElement = Exclude<
  Element,
  Polyline | Symbol | Group
>;

export const isRectLikeElement = (
  element: Element,
): element is RectLikeElement => {
  return (
    element.type === ElementType.Rect ||
    element.type === ElementType.Text ||
    element.type === ElementType.Fixture ||
    element.type === ElementType.Triangle ||
    element.type === ElementType.Trapezoid ||
    element.type === ElementType.Angle
  );
};

export type Change = {
  changeId?: number;
  canvasId?: string;
  createdAt: string;
  changeType: ChangeType;
  elementType: ElementType;
  object: Element;
  ephemeral: boolean;
};

export interface UserVersion {
  userId: string;
  version: number;
}

export interface VersionVector {
  [id: string]: UserVersion;
}

export interface Cursor {
  id: string;
  username: string;
  position: {
    x: number;
    y: number;
  };
  lastUpdated: string; // ISO-timestamp
  color: string;
}

export interface CursorEvent {
  type: "cursor";
  data: Cursor[];
}

export interface ChangeEvent {
  type: "changes";
  batchId: string;
  data: Change[];
}

export interface PingEvent {
  type: "ping";
  data: { timestamp: number };
}

export interface PongEvent {
  type: "pong";
  data: { timestamp: number };
}

export interface AckEvent {
  type: "ack";
  batchId: string;
}

export interface SyncReadyEvent {
  type: "sync_ready";
}

export type SocketEvent =
  CursorEvent | ChangeEvent | PingEvent | PongEvent | AckEvent | SyncReadyEvent;

export type CommuneData = {
  slug: string;
  name: string;
  county: string;
  municipalityUrl: string;
  bygglovUrl: string;
};

import kommunerJson from "./kommun/kommuner.json";

export const kommuner = kommunerJson as CommuneData[];

export * from "./units/scaling";
export * from "./units/units";
